import { CalendarBlankIcon } from '@phosphor-icons/react';

import { currentLocale } from '@web/i18n';
import {
  Button,
  Calendar,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@web/ui/core/atoms';

const DEFAULT_TIME = '09:00';

function ymd(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Date + time picker: Calendar in a Popover + a time input. `value` and the
 * `onChange` payload are datetime-local strings ("YYYY-MM-DDTHH:mm") or "".
 * Generic/reusable — callers pass `placeholder`/`ariaLabel`.
 */
export function DateTimePicker({
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
}: Readonly<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}>): React.JSX.Element {
  const datePart = value.slice(0, 10);
  const timePart = value.slice(11, 16);
  const selected = value ? new Date(value) : undefined;
  const label =
    value && selected
      ? new Intl.DateTimeFormat(currentLocale(), {
          dateStyle: 'short',
          timeStyle: 'short',
        }).format(selected)
      : (placeholder ?? '');

  const emitDate = (d: Date | undefined): void => {
    if (!d) return;
    onChange(`${ymd(d)}T${timePart || DEFAULT_TIME}`);
  };

  const emitTime = (time: string): void => {
    if (!time) return;
    onChange(`${datePart || ymd(new Date())}T${time}`);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled} aria-label={ariaLabel}>
          <CalendarBlankIcon size={14} weight="bold" aria-hidden />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent role="dialog">
        <Calendar mode="single" selected={selected} defaultMonth={selected} onSelect={emitDate} />
        <div className="border-t border-border-soft p-3">
          <Input
            type="time"
            size="sm"
            aria-label="time"
            value={timePart}
            onChange={(e) => {
              emitTime(e.target.value);
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
